/** @jsx jsx */

import { Block } from '@chameleoncreator/creator-app';
import { Stream } from '@cloudflare/stream-react';
import { css, jsx } from '@emotion/core';
import playVideo from 'assets/playVideo.svg';
import VideoIcon from 'assets/Video.svg';
import { interactionContext } from 'components/lib/TextAndImage/interactionContext';
import EditMarker from 'components/UI/EditMarker';
import { keyframes } from 'emotion';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import {
  Fragment as _,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactPlayer from 'react-player';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { killAllAudio } from 'services/killAllAudio';
import { unlockInteraction } from 'services/unlockInteraction';
import { colors, getButtonFocus } from 'styles/variables';
import Transcript from './Transcript';

const { lPink } = colors;

const frameSelected = keyframes`
  from {
    width: 50%;
    height: 50%;
  }
  to {
    width: 75%;
    height: 75%;
  }
`;

const ShowTranscriptBtn = ({
  id,
  setShowTranscript,
  showTranscriptFontSize = 12,
  showTranscriptFontColor = '#000',
  showTranscriptLabel = 'Show transcript',
}) => {
  return (
    <button
      id={`show-transcript-${id}`}
      css={css`
        width: 100%;

        font-size: ${showTranscriptFontSize}px;

        border: none;
        outline: none;
        background: transparent;

        text-decoration: underline;
        text-align: center;
        cursor: pointer;

        color: ${showTranscriptFontColor};
        &:focus {
          ${getButtonFocus('#2684FF')}
        }
      `}
      onClick={() => {
        setShowTranscript(true);
      }}
    >
      {showTranscriptLabel}
    </button>
  );
};

const EditWrapper = ({ id, selected }) => {
  return (
    <div
      css={css`
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        left: 0px;
        top: 0px;
        width: 100%;
        height: 100%;
        pointer-events: none;
      `}
    >
      <div
        css={css`
                position: relative;
                display: flex;
                justify-content: center;
                align-items: center;
                width: ${selected ? '75%' : '50%'};
                height: ${selected ? '75%' : '50%'};

                ${selected && `animation: ${frameSelected} 0.2s forwards;`}

                ${!selected &&
                  `
                background: rgba(0, 0, 0, 0.1);
                `}

                box-shadow: ${
                  selected ? `0 0 0 1px ${lPink}` : '0 0 0 1px lightGrey'
                };
                transition: all 0.2s;
                pointer-events: all;
                cursor: pointer;
                &:hover {
                  ${!selected && `box-shadow: 0 0 0 1px ${lPink};`}
                }
            `}
        onClick={e => {
          e.stopPropagation();
          if (selected) return;
          setCurrentBlock({ id });
        }}
      >
        {!selected && (
          <img src={VideoIcon} style={{ width: 50, opacity: 0.5 }} />
        )}

        {!selected && <EditMarker />}
      </div>
    </div>
  );
};

type IProps = {
  src: string;
  videoSrc: string;
  maxWidth: number;
  hideControls: boolean;
  useTranscript: boolean;
  showTranscriptFontSize: number;
  showTranscriptFontColor: string;
  showTranscriptLabel: string;
  viewingTranscriptUnlocksInteraction: boolean;
  cloudflareUid: string;
  posterSrc: boolean;
  autoPlay: boolean;
  playInline: boolean;
};

const Video = ({
  id,
  focusedNodeId,
  children,
  ...props
}: {
  id?: any;
  focusedNodeId?: any;
  children?: any;
}) => {
  const { entry, init } = useIntersectionObserver(true);
  const {
    src,
    videoSrc,
    maxWidth = 960,
    useTranscript,
    showTranscriptFontSize = 12,
    showTranscriptFontColor = '#000',
    showTranscriptLabel = 'Show transcript',
    viewingTranscriptUnlocksInteraction = false,
    cloudflareUid,
    // cloudflareUid = '5cf27df9948930e8611e2a27c584b40e',
    posterSrc,
  } = props as IProps;

  // defaults
  let {
    hideControls = false,
    autoPlay = false,
    playInline = false,
  } = props as IProps;

  // forced false in dev
  if (window.dev()) {
    hideControls = false;
    autoPlay = false;
    playInline = false;
  }

  const isYouTube = (videoSrc || src).match(
    /^(https?\:\/\/)?((www\.)?youtube\.com|youtu\.be)\/.+$/
  );

  const [isPlaying, setIsPlaying] = useState(false);
  const [calcHeight, setCalcHeight] = useState(maxWidth * 0.5625);

  const [showTranscript, setShowTranscript] = useState(false);
  const [showPoster, setShowPoster] = useState<boolean | null>(posterSrc);

  const selected = focusedNodeId === id;
  const context = useContext(interactionContext)! as Block;
  const mounted = useRef(false);
  const video = useRef<HTMLDivElement>(null);

  // need to get the correct type for player
  const player = useRef<any>(null);

  const hasAutoPlayed = useRef<boolean>(false);

  useLayoutEffect(() => {
    if (!mounted.current) {
      init([video.current], {
        threshold: 0.5,
      });
    }

    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const onWindowResize = () => {
      const { width } = video.current!.getBoundingClientRect();
      const calcHeightTarget = width * 0.5625;

      if (calcHeight === calcHeightTarget) {
        return;
      }

      setCalcHeight(calcHeightTarget);
    };

    onWindowResize();
    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  });

  useEffect(() => {
    // pauses cloudflare player
    //
    if (!cloudflareUid || !player.current) {
      return;
    }

    if (isPlaying && entry && !entry.isIntersecting) {
      player.current.pause();
    }
  }, [entry?.isIntersecting]);

  useEffect(() => {
    if (isPlaying) {
      killAllAudio();
    }
  }, [isPlaying]);

  useEffect(() => {
    const onFullScreenChange = e => {
      if (!document.fullscreenElement && video?.current) {
        // this specific selector fails when video is added as media to interactions NOT
        // a standalone video interaction
        // const section = document.querySelector(`#i${context.id}`);

        // let's be more generic and get whichever section element is closest to the video
        const section = video.current.closest('section');
        section?.scrollIntoView();
      }
    };

    video?.current?.addEventListener('fullscreenchange', onFullScreenChange);

    return () => {
      video?.current?.removeEventListener(
        'fullscreenchange',
        onFullScreenChange
      );
    };
  }, []);

  useEffect(() => {
    if (viewingTranscriptUnlocksInteraction && showTranscript) {
      unlockInteraction(context.id);
    }
  }, [showTranscript]);

  useEffect(() => {
    if (!autoPlay) {
      return;
    }

    if (entry?.isIntersecting && !hasAutoPlayed.current) {
      // console.log('browser allows autoPlay !?!?');
      //

      cloudflareUid ? player?.current?.play() : setIsPlaying(true);
      hasAutoPlayed.current = true;
    }
  }, [entry?.isIntersecting]);

  useEffect(() => {
    if (entry?.isIntersecting) {
      return;
    }

    // reset the hasAutoPlayed
    if (autoPlay) {
      hasAutoPlayed.current = false;
    }
  }, [entry?.isIntersecting]);

  const continuePlayingInFullscreen =
    isPlaying && document.fullscreenElement !== null ? true : false;

  const videoPlayer = (
    <div
      ref={video}
      className="video-player-container"
      css={css`
        position: relative;
        width: 100%;
        max-width: ${maxWidth}px;
        // height: ${calcHeight ? `${calcHeight}px` : '100%'};
        // margin: 50px 0;
      `}
      // i want to keep this on the periphery i think
      // if we're not happy with the behaviours we're getting out of the box for these players
      // ( reactPlayer feels kinda janky )
      // if we roll our own solution we can standardise the output
      // onMouseOver={() => {
      //   setShowPoster(false);
      // }}
      // onMouseOut={() => {
      //   if (player.current.getCurrentTime() === 0) {
      //     setShowPoster(true);
      //   }
      // }}
    >
      <_>
        {cloudflareUid ? (
          <CloudflareVideo
            streamRef={player}
            id={id}
            cloudflareUid={cloudflareUid}
            height={calcHeight ? `${calcHeight}px` : '100%'}
            onPlay={() => {
              setIsPlaying(true);
            }}
            onPause={() => {
              setIsPlaying(false);
            }}
            onEnded={() => {
              unlockInteraction(context.id);
            }}
            controls={!hideControls}
            poster={posterSrc}
            {...(playInline && {
              autoplay: true,
              loop: true,
              muted: true,
              playsinline: true,
              controls: false,
            })}
            {...(entry?.isIntersecting &&
              autoPlay && {
                autoplay: true,
              })}
          />
        ) : (
          <ReactPlayer
            ref={player}
            url={videoSrc || src}
            width="100%"
            height={calcHeight || '100%'}
            onStart={() => {}}
            onPlay={() => {
              setIsPlaying(true);
            }}
            onPause={() => {
              setIsPlaying(false);
            }}
            onEnded={() => {
              unlockInteraction(context.id);
            }}
            playing={
              continuePlayingInFullscreen ||
              (isPlaying && entry && entry.isIntersecting)
            }
            controls={!hideControls}
            light={posterSrc}
            {...(playInline && {
              playing: true,
              loop: true,
              muted: true,
              playsinline: true,
              controls: false,
            })}
            // {...(!window.dev() &&
            //   autoPlay && {
            //     playing: true,
            //   })}
          />
        )}
      </_>

      {!window.dev() && (
        <_>
          {hideControls && !isYouTube && (
            <div
              style={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={() => {
                hasAutoPlayed.current = true;

                if (cloudflareUid) {
                  // play for cloudflare. . .

                  player?.current[isPlaying ? 'pause' : 'play']();
                  return;
                }

                setIsPlaying(isPlaying => !isPlaying);
              }}
            >
              {!isPlaying && (
                <div
                  className="fader_-2s"
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    border: '2px solid #000',
                    background: `url('${playVideo}') no-repeat`,
                    backgroundPosition: 'center',
                    boxShadow: '0 0 10px rgba(0,0,0, 0.2)',
                  }}
                />
              )}
            </div>
          )}
        </_>
      )}

      {window.dev() && <EditWrapper id={id} selected={selected} />}
    </div>
  );

  const showTranscriptBtn = (
    <div style={{ margin: '5px 0 0 0' }}>
      <ShowTranscriptBtn
        id={id}
        setShowTranscript={setShowTranscript}
        showTranscriptFontSize={showTranscriptFontSize}
        showTranscriptFontColor={showTranscriptFontColor}
        showTranscriptLabel={showTranscriptLabel}
      />
    </div>
  );

  return (
    <_>
      {window.dev() ? (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            left: 0px;
            top: 0px;
            width: 100%;
            // height: ${calcHeight}px;

            // * {
            //   max-height: ${calcHeight}px;
            // }
          `}
        >
          {videoPlayer}

          {useTranscript && showTranscriptBtn}
        </div>
      ) : (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
          `}
        >
          {videoPlayer}

          {useTranscript && showTranscriptBtn}
        </div>
      )}

      {showTranscript && (
        <Transcript
          onClose={() => {
            const tick = setInterval(() => {
              const showTranscriptButtonElement: any = document.querySelector(
                `#show-transcript-${id}`
              );

              if (showTranscriptButtonElement) {
                showTranscriptButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            setShowTranscript(false);
          }}
        >
          {children}
        </Transcript>
      )}
    </_>
  );
};

export const CloudflareVideo = props => {
  const { id, cloudflareUid, ...rest } = props;
  const { height } = rest;

  return (
    <div
      css={css({
        height: height,
        '*': {
          maxHeight: height,
          // pointerEvents: window.dev() ? 'none' : 'auto',
        },
      })}
    >
      <Stream
        controls
        src={cloudflareUid}
        {...{
          ...rest,
          //
        }}
      />
    </div>
  );
};

export default Video;
